/**
 * TimerClient: local engine + persistence + server reconciliation.
 *
 * Reconciliation strategy (see ARCHITECTURE.md):
 *  1. Every local user action is applied immediately (engine), persisted and
 *     emitted to the UI. Only then is it sent to the server, if connected and
 *     authorised. The UI never waits for the server.
 *  2. On every (re)connect the client sends `hello` including its current
 *     state. The server keeps whichever state is newer and answers/broadcasts.
 *  3. An incoming server state replaces the local state only if `isNewer`.
 *     Equal or older states are ignored, so the local time anchor is left
 *     untouched: a reconnect can never make a running timer jump or restart.
 *  4. Adopting a newer state re-anchors `endAt` on this device's clock from
 *     the transmitted `remainingMs`.
 */
import { createInitialState, isNewer, type TimerState } from '../../shared/state.js';
import { applyCommand, type Command } from '../../shared/timer-engine.js';
import { snapshotAt, type TimerSnapshot } from '../../shared/format.js';
import {
  fromWire,
  toWire,
  PROTOCOL_VERSION,
  type ClientMessage,
  type ErrorCode,
  type Presence,
  type Role,
  type ServerMessage,
} from '../../shared/protocol.js';
import type { AppStorage } from './storage.js';

export interface Transport {
  send(msg: ClientMessage): boolean;
  isOpen(): boolean;
}

export type ConnectionStatus = 'connecting' | 'open' | 'offline' | 'bad_room';

export interface TimerClientOptions {
  role: Role;
  clientId: string;
  room: string;
  /** Called on every hello; returns the PIN to send (null = read-only). */
  getPin: () => string | null;
  storage: AppStorage;
  now?: () => number;
}

export interface ClientEvent {
  type: 'state' | 'connection' | 'error';
  error?: ErrorCode;
}

export class TimerClient {
  state: TimerState;
  connection: ConnectionStatus = 'offline';
  canControl = false;
  presence: Presence = { displays: 0, controls: 0 };
  lastError: ErrorCode | null = null;

  private transport: Transport | null = null;
  private readonly listeners = new Set<(ev: ClientEvent) => void>();
  private readonly now: () => number;

  constructor(private readonly opts: TimerClientOptions) {
    this.now = opts.now ?? (() => Date.now());
    // Reload safety: restore the persisted state; a running timer resumes from its anchor.
    this.state = opts.storage.loadState() ?? createInitialState();
  }

  attachTransport(transport: Transport): void {
    this.transport = transport;
  }

  subscribe(listener: (ev: ClientEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  snapshot(now = this.now()): TimerSnapshot {
    return snapshotAt(this.state, now);
  }

  /** True when a command sent now will actually reach the server. */
  isSynced(): boolean {
    return this.connection === 'open' && this.canControl && this.transport?.isOpen() === true;
  }

  /** Apply a local user action. Works fully offline. */
  dispatch(command: Command): 'applied' | 'noop' {
    const next = applyCommand(this.state, command, this.now(), this.opts.clientId);
    if (next === null) return 'noop';
    this.state = next;
    this.opts.storage.saveState(next);
    this.emit({ type: 'state' });
    this.pushState();
    return 'applied';
  }

  helloMessage(): ClientMessage {
    const pin = this.opts.getPin();
    const msg: ClientMessage = {
      type: 'hello',
      protocol: PROTOCOL_VERSION,
      room: this.opts.room,
      role: this.opts.role,
      clientId: this.opts.clientId,
      state: toWire(this.state, this.now()),
    };
    if (pin) msg.pin = pin;
    return msg;
  }

  handleConnecting(): void {
    if (this.connection === 'bad_room') return;
    this.connection = 'connecting';
    this.emit({ type: 'connection' });
  }

  handleOpen(): void {
    this.transport?.send(this.helloMessage());
  }

  handleClose(): void {
    if (this.connection !== 'bad_room') this.connection = 'offline';
    this.canControl = false;
    this.presence = { displays: 0, controls: 0 };
    this.emit({ type: 'connection' });
  }

  handleMessage(msg: ServerMessage): void {
    switch (msg.type) {
      case 'welcome':
        this.connection = 'open';
        this.canControl = msg.canControl;
        this.presence = msg.presence;
        // Keep a `bad_pin` error visible until a PIN is accepted.
        if (msg.canControl) this.lastError = null;
        this.emit({ type: 'connection' });
        break;
      case 'state': {
        const incoming = fromWire(msg.state, this.now());
        // Old, duplicate or identical states are ignored; anchor stays untouched.
        if (!isNewer(incoming, this.state)) return;
        this.state = incoming;
        this.opts.storage.saveState(incoming);
        this.emit({ type: 'state' });
        break;
      }
      case 'presence':
        this.presence = msg.presence;
        this.emit({ type: 'connection' });
        break;
      case 'ping':
        this.transport?.send({ type: 'pong' });
        break;
      case 'error':
        this.lastError = msg.code;
        if (msg.code === 'bad_room') this.connection = 'bad_room';
        if (msg.code === 'bad_pin') this.opts.storage.clearPin();
        this.emit({ type: 'error', error: msg.code });
        break;
    }
  }

  private pushState(): void {
    if (!this.isSynced()) return;
    this.transport?.send({ type: 'state', state: toWire(this.state, this.now()) });
  }

  private emit(ev: ClientEvent): void {
    for (const l of this.listeners) {
      try {
        l(ev);
      } catch (err) {
        console.error('listener failed', err);
      }
    }
  }
}
